import { createElement, isValidElement, ReactNode } from 'react';
import { I18nProvider } from 'react-admin';

import { ENGLISH_WORDS_PATTERN } from '@sh4res/bo-common/i18n/englishWordsInFrench';

/**
 * Wraps an existing i18n provider to automatically handle mixed language accessibility.
 *
 * This function enhances the translate function to automatically wrap English words
 * with proper lang attributes when they appear in French text.
 *
 * @param originalProvider - The original i18n provider (e.g., from polyglotI18nProvider)
 * @returns Enhanced i18n provider with automatic accessibility
 */
export function createAccessibleI18nProvider(originalProvider: I18nProvider): I18nProvider {
  return {
    ...originalProvider,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- options is defined as any in ra-core/src/types.ts
    translate: (key: string, options?: any) => {
      const originalText = originalProvider.translate(key, options);

      if (typeof originalText !== 'string' || !ENGLISH_WORDS_PATTERN.test(originalText)) {
        return originalText;
      }

      // TypeScript expects Translate to return only string, but we return ReactNode[]
      // for accessibility. React components handle ReactNode[] perfectly fine.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-return
      return processTextWithLanguageAttributes(originalText) as any;
    },
  };
}

/**
 * Plain text of a `translate()` result. Use it wherever a translation is handled as a string
 * (searching, sorting, comparing...), since the provider above may return ReactNode[] instead.
 */
export function translationToText(translation: ReactNode): string {
  if (typeof translation === 'string' || typeof translation === 'number') {
    return String(translation);
  }
  if (Array.isArray(translation)) {
    return translation.map(translationToText).join('');
  }
  if (isValidElement<{ children?: ReactNode }>(translation)) {
    return translationToText(translation.props.children);
  }

  return '';
}

function processTextWithLanguageAttributes(text: string): React.ReactNode[] {
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;

  ENGLISH_WORDS_PATTERN.lastIndex = 0;

  let match;
  while ((match = ENGLISH_WORDS_PATTERN.exec(text)) !== null) {
    const { index } = match;
    if (index > lastIndex) {
      parts.push(text.slice(lastIndex, index));
    }

    parts.push(
      createElement(
        'span',
        {
          key: `en-word-${index}`,
          lang: 'en',
        },
        match[0],
      ),
    );

    const { lastIndex: patternLastIndex } = ENGLISH_WORDS_PATTERN;
    lastIndex = patternLastIndex;
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  ENGLISH_WORDS_PATTERN.lastIndex = 0;

  return parts;
}
